import { useContext, useEffect, useState } from 'react';

import { useErrorBoundary } from 'react-error-boundary';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReferralContext } from '@contexts/referral-context';
import { observer } from 'mobx-react-lite';

import { sendAnalyticEvent } from '@app/web-analytic';

import { AnalyticEvent, CANDIDATE_AUTH } from '@shared/constants';
import {
  BoundaryErrorType,
  ConsentType,
  FlkCode,
  FullWidthPrimaryButton,
  addTestAttribute,
  getMismatches,
  useStores,
} from '@shared/index';

import {
  type CreateCandidateRequestParams,
  useCreateCandidate,
} from '../../api';
import { useCandidateRegistrationSearchParams } from '../../hooks';
import {
  CandidateRegistrationStep,
  CandidateRegistrationTab,
  type CreatePasswordFormParams,
} from '../../model';

export const SubmitCreatePasswordForm = observer(() => {
  const { t } = useTranslation();
  const { showBoundary } = useErrorBoundary();
  const navigate = useNavigate();
  const { createReferralSearchParams } = useCandidateRegistrationSearchParams();
  const {
    MainStore: {
      agentUserStore: { setConfirmToken, newCandidate },
    },
  } = useStores();
  const { referralData } = useContext(ReferralContext);
  const { refMagCode, refDeptCode, refDeptId, refBranchId, linkId } =
    referralData;

  const {
    handleSubmit,
    formState: { errors },
    setError,
  } = useFormContext<CreatePasswordFormParams>();

  const [requestParams, setRequestParams] =
    useState<CreateCandidateRequestParams>({
      candidate: {
        nickname: newCandidate?.nickname || '',
        phone: newCandidate?.phone || '',
        password: '',
      },
      consents: [],
    });

  const { res, refetch, error, isFetching } = useCreateCandidate(
    {
      ...requestParams,
    },
    [requestParams]
  );

  useEffect(() => {
    if (
      !!requestParams?.candidate?.nickname?.length &&
      !!requestParams?.candidate?.phone?.length &&
      !!requestParams?.candidate?.password?.length &&
      !!requestParams.consents.length
    ) {
      refetch();
    }
  }, [requestParams]);

  useEffect(() => {
    if (!isFetching && res) {
      sendAnalyticEvent(AnalyticEvent.CANDIDATE_REG_PASSWORD_SUCCESSINVENTE);

      setConfirmToken(res.token);

      const { search } = createReferralSearchParams({
        step: CandidateRegistrationStep.OTP,
        tab: CandidateRegistrationTab.REGISTRATION,
        refDeptCode,
        refMagCode,
        refDeptId,
        refBranchId,
        linkId,
      });

      navigate({ pathname: CANDIDATE_AUTH, search });
    }
  }, [
    isFetching,
    res,
    refDeptCode,
    refMagCode,
    refDeptId,
    refBranchId,
    linkId,
  ]);

  useEffect(() => {
    if (error) {
      sendAnalyticEvent(AnalyticEvent.CANDIDATE_REG_PASSWORD_ERRORINVENTE);
      const mismatches = getMismatches(error);

      if (!!mismatches) {
        let hasKnownErrors = false;
        const passwordValidationErrorMessages: string[] = [];
        mismatches.forEach(({ code, message }) => {
          if (
            code === FlkCode.RMM_6050_0015 ||
            code === FlkCode.RMM_6050_0018 ||
            code === FlkCode.RMM_6050_0023 ||
            code === FlkCode.RMM_6050_0024 ||
            code === FlkCode.RMM_6050_0025
          ) {
            if (message) passwordValidationErrorMessages.push(message);
            hasKnownErrors = true;
          }
        });

        if (passwordValidationErrorMessages.length) {
          setError('root.validationError', {
            message: passwordValidationErrorMessages.join('\n'),
          });
        }

        if (!hasKnownErrors) {
          showBoundary(BoundaryErrorType.SomethingWentWrong);
        }
      } else {
        showBoundary(BoundaryErrorType.SomethingWentWrong);
      }
    }
  }, [error]);

  const submitPage = handleSubmit((values) => {
    const { password } = values;

    setRequestParams((prevParams) => ({
      ...prevParams,
      candidate: {
        ...prevParams.candidate,
        password,
      },
      consents: [
        {
          consentType: ConsentType.AgentDataManagement,
          approvedAt: new Date().toISOString(),
        },
      ],
    }));
  });

  return (
    <FullWidthPrimaryButton
      size='l'
      onClick={submitPage}
      isLoading={isFetching}
      disabled={!!Object.keys(errors).length}
      {...addTestAttribute('candidate-create-password-form-submit')}
    >
      {t('COMMON:buttons.continue') || ''}
    </FullWidthPrimaryButton>
  );
});
